import { useEffect, useState } from 'react'
import { radius, theme, TAP_MIN } from '../theme'
import {
  music, onSoundChange, setMusicOn, setMusicVolume, setSfxOn, setSfxVolume, sfx, soundSettings,
} from '../lib/sound'

/**
 * Small buttons, music and sound effects, always in the same corner while a
 * child is in their part of the app, and a third that opens two volume
 * sliders.
 *
 * Separate switches because they are separate wishes: plenty of children want
 * the chime and not the tune, and a parent in the next room may want neither.
 * Separate volumes since 18 Oct 2026 (Sam): turning the whole device down to
 * quieten the music made the spoken words quiet too. The voice is not on
 * either slider, so it stays at the device's volume. Remembered on this
 * device, so a child who turned the music down finds it down tomorrow.
 *
 * Big enough to hit (TAP_MIN), out of the way of the answers, and labelled for
 * a screen reader rather than relying on the picture.
 */
export function SoundControls({ wantsMusic }: { wantsMusic: boolean }) {
  const [settings, setSettings] = useState(soundSettings)
  const [open, setOpen] = useState(false)

  useEffect(() => onSoundChange(() => setSettings(soundSettings())), [])

  // Music belongs to the child's spaces only. Leaving them — to Home, to a
  // grown-up's settings — fades it out.
  useEffect(() => {
    if (wantsMusic && settings.music) music.start()
    else music.stop()
  }, [wantsMusic, settings.music])

  useEffect(() => () => music.stop(), [])

  // Each icon carries its name underneath: a tablet has no hover, so a
  // tooltip is never seen (build list item 5, 25 Oct 2026).
  const button = (on: boolean, label: string, icon: string, iconOff: string,
                  toggle: () => void, caption = label) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      aria-label={`${label}: ${on ? 'on' : 'off'}`}
      title={`${label}: ${on ? 'on' : 'off'}`}
      style={{
        width: TAP_MIN, height: TAP_MIN,
        borderRadius: radius.pill,
        border: `2px solid ${on ? theme.green : theme.border}`,
        background: on ? theme.greenPale : theme.card,
        fontSize: 20, lineHeight: 1,
        cursor: 'pointer',
        opacity: on ? 1 : 0.7,
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
      }}
    >
      {on ? icon : iconOff}
    </button>
    <span aria-hidden="true" style={{ fontSize: 11, fontWeight: 700, color: theme.muted }}>{caption}</span>
    </div>
  )

  const slider = (label: string, value: number, set: (v: number) => void, after?: () => void) => (
    <label style={{ display: 'block', fontSize: 14, fontWeight: 700, color: theme.text2 }}>
      {label} <span style={{ color: theme.muted, fontWeight: 600 }}>{Math.round(value * 100)}%</span>
      <input
        type="range" min={0} max={100} step={5}
        value={Math.round(value * 100)}
        onChange={(e) => set(Number(e.target.value) / 100)}
        onPointerUp={after}
        onKeyUp={after}
        aria-label={`${label} volume`}
        style={{ display: 'block', width: '100%', marginTop: 4, accentColor: theme.green }}
      />
    </label>
  )

  return (
    <div style={{
      position: 'fixed', right: 14, bottom: 14, zIndex: 40,
      display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8,
    }}>
      {open ? (
        <div role="group" aria-label="Volume" style={{
          width: 220, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12,
          background: theme.card, border: `1.5px solid ${theme.border}`, borderRadius: radius.lg,
          boxShadow: '0 6px 18px rgba(0,0,0,0.14)',
        }}>
          {slider('Music', settings.musicVolume, setMusicVolume)}
          {slider('Sounds', settings.sfxVolume, setSfxVolume, () => sfx.tap())}
          <div style={{ fontSize: 12, color: theme.muted, lineHeight: 1.4 }}>
            Spoken words stay at the device’s volume.
          </div>
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8 }}>
        {button(settings.music, 'Music', '🎵', '🔇',
          () => setMusicOn(!settings.music))}
        {button(settings.sfx, 'Sound effects', '🔔', '🔕',
          () => setSfxOn(!settings.sfx), 'Sounds')}
        {button(open, 'Volume', '🎚️', '🎚️', () => setOpen(!open))}
      </div>
    </div>
  )
}
